// Utility functions for filtering and searching data
import { format } from 'date-fns';

export interface FilterOptions {
  search?: string;
  status?: string[];
  type?: string[];
  dateRange?: {
    start: string;
    end: string;
  };
  location?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Generic search function that works with any data type
export const searchData = <T extends Record<string, any>>(
  data: T[],
  searchTerm: string,
  searchFields: (keyof T | string)[]
): T[] => {
  if (!searchTerm || searchTerm.length < 2) return data;

  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return data.filter(item => {
    return searchFields.some(field => {
      const value = getNestedValue(item, field as string);
      return value && String(value).toLowerCase().includes(lowerSearchTerm);
    });
  });
};

// Generic filter function for status, type, etc.
export const filterData = <T extends Record<string, any>>(
  data: T[],
  filters: FilterOptions
): T[] => {
  let filteredData = [...data];

  // Apply search filter
  if (filters.search) {
    // This should be implemented per data type since search fields vary
  }

  // Apply status filter
  if (filters.status && filters.status.length > 0) {
    filteredData = filteredData.filter(item => 
      filters.status!.includes(item.status)
    );
  }

  // Apply type filter
  if (filters.type && filters.type.length > 0) {
    filteredData = filteredData.filter(item => 
      filters.type!.includes(item.type)
    );
  }

  // Apply date range filter
  if (filters.dateRange) {
    filteredData = filteredData.filter(item => {
      const itemDate = new Date(item.createdAt || item.date || item.startDate);
      const startDate = new Date(filters.dateRange!.start);
      const endDate = new Date(filters.dateRange!.end);
      return itemDate >= startDate && itemDate <= endDate;
    });
  }

  // Apply location filter
  if (filters.location && filters.location.length > 0) {
    filteredData = filteredData.filter(item => 
      filters.location!.some(location => 
        (item.location || item.pickupLocation || '').toLowerCase().includes(location.toLowerCase())
      )
    );
  }

  return filteredData;
};

// Generic sort function
export const sortData = <T extends Record<string, any>>(
  data: T[],
  sortBy: string,
  sortOrder: 'asc' | 'desc' = 'asc'
): T[] => {
  return [...data].sort((a, b) => {
    const aValue = getNestedValue(a, sortBy);
    const bValue = getNestedValue(b, sortBy);

    let comparison = 0;
    
    if (aValue < bValue) {
      comparison = -1;
    } else if (aValue > bValue) {
      comparison = 1;
    }

    return sortOrder === 'desc' ? comparison * -1 : comparison;
  });
};

// Helper function to get nested object values (e.g., 'user.name')
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// CSV export utility
export const exportToCSV = (data: any[], filename: string, headers?: string[]) => {
  if (data.length === 0) {
    alert('No data to export');
    return;
  }

  const csvHeaders = headers || Object.keys(data[0]);
  const csvContent = [
    csvHeaders.join(','),
    ...data.map(row => 
      csvHeaders.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV values
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

// Print utility
export const printData = (title: string, data: any[], columns: string[]) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print');
    return;
  }

  const tableRows = data.map(item => 
    `<tr>${columns.map(col => `<td>${item[col] || ''}</td>`).join('')}</tr>`
  ).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        h1 { color: #1976d2; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f5f5f5; font-weight: bold; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        @media print { body { margin: 0; } }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <p>Generated: ${new Date().toLocaleString()}</p>
      <table>
        <thead>
          <tr>${columns.map(col => `<th>${col}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.print();
};

// Generate status color helper
export const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const statusLower = status.toLowerCase();
  
  if (['active', 'confirmed', 'completed', 'verified', 'paid', 'processed'].includes(statusLower)) {
    return 'success';
  }
  
  if (['pending', 'in_progress', 'processing', 'review'].includes(statusLower)) {
    return 'warning';
  }
  
  if (['cancelled', 'failed', 'rejected', 'suspended', 'error'].includes(statusLower)) {
    return 'error';
  }
  
  if (['draft', 'scheduled', 'maintenance'].includes(statusLower)) {
    return 'info';
  }
  
  return 'default';
};

// Format currency
export const formatCurrency = (amount: number, currency: string = 'NGN'): string => {
  const symbol = currency === 'NGN' ? '₦' : '$';
  return `${symbol}${amount.toLocaleString()}`;
};

// Format date
export const formatDate = (date: string | Date): string => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  return dateObj.toLocaleDateString();
};

// Format date and time
export const formatDateTime = (date: string | Date): string => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  return dateObj.toLocaleString();
};

// Safe format function that validates dates before formatting
// Use this wrapper instead of date-fns format to avoid "Invalid time value" errors
export const safeFormatDate = (date: string | Date | null | undefined, formatStr: string, fallback: string = 'N/A'): string => {
  if (!date) return fallback;
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return fallback;

  try {
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
  }
};